import { Link, createFileRoute, notFound } from "@tanstack/react-router";
import {
  CheckIcon,
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ClockIcon,
  CopyIcon,
  CrossIcon,
  Link2,
  LockIcon,
  PlayIcon,
  SaveOffIcon,
  Share2,
  Users,
} from "lucide-react";
import * as z from "zod";
import { zodValidator } from "@tanstack/zod-adapter";
import {
  useMutation,
  useQuery,
  useQueryClient,
  useSuspenseQuery,
} from "@tanstack/react-query";
import {
  startTransition,
  use,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { HTTPError } from "ky";
import { toast } from "sonner";
import { differenceInSeconds, isPast } from "date-fns";
import * as duration from "duration-fns";
import { flushSync } from "react-dom";
import { StickToBottom, useStickToBottomContext } from "use-stick-to-bottom";
import { ShareMeetingDialog } from "../-components/ShareMeetingDialog";
import { MeetingDetailTabs } from "../-components/meeting-detail/MeetingDetailTabs";
// import { MeetingOverallCard } from "../-components/meeting-detail/MeetingOverallCard";
import { MeetingParticipantsCard } from "../-components/meeting-detail/MeetingParticipantsCard";
import { MeetingSummaryPanel } from "../-components/meeting-detail/MeetingSummaryPanel";
import { TranscriptionTimeline } from "../-components/meeting-detail/TranscriptionTimeline";
import { formatStateGroupLabel } from "../-components/meeting-detail/meeting-detail-utils";
import { MeetingProvider, useMeeting } from "../-hooks/meeting";
import type { ComponentRef } from "react";
import type { MeetingBot } from "@/lib/queries/meetings";
import type { MeetingDetailTab } from "../-components/meeting-detail/MeetingDetailTabs";
import { LoadingSpinner } from "@/components/loading";
import {
  meetingBotQueryOptions,
  meetingSummaryQueryOptions,
  useRejoinMeetingMutation,
} from "@/lib/queries/meetings";
import { userInfoQueryOptions } from "@/lib/queries/auth";
import { isGuest } from "@/lib/perms";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { invariant } from "@/lib/invariant";
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "@/components/ui/empty";
import { VideoContext } from "@/routes/_app/meetings/-state";
import {
  formatMeetingTimestamp,
  normalizedDifferenceDuration,
} from "@/lib/utils/duration";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Skeleton } from "@/components/ui/skeleton";
import PreparationImage from "@/data/images/state-groups/Preparation.svg";
import PreparationDarkImage from "@/data/images/state-groups/PreparationDark.svg";
import ProcessImage from "@/data/images/state-groups/Process.svg";
import ErrorImage from "@/data/images/state-groups/Error.svg";
import ErrorDarkImage from "@/data/images/state-groups/ErrorDark.svg";
import RestrictedImage from "@/data/images/state-groups/Restricted.svg";
import RestrictedDarkImage from "@/data/images/state-groups/RestrictedDark.svg";
import ScheduledImage from "@/data/images/state-groups/Scheduled.svg";
import ScheduledDarkImage from "@/data/images/state-groups/ScheduledDark.svg";
import { useTheme } from "@/components/themes/provider";
import { fetchAllMeetingUtterances } from "@/lib/queries/utterances";

const paramsSchema = z.object({
  meetingId: z.uuid(),
});

const searchSchema = z.object({
  from: z.enum(["shared"]).optional(),
  scope: z.enum(["own", "organization"]).optional(),
});

export const Route = createFileRoute("/_app/meetings/$meetingId/")({
  params: { parse: paramsSchema.parse },
  validateSearch: zodValidator(searchSchema),
  staticData: {
    isProblematicScrollPage: true,
  },
  component: RouteComponent,
  async loader({ params: { meetingId }, context: { queryClient } }) {
    try {
      await queryClient.ensureQueryData(
        meetingBotQueryOptions(meetingId, {
          include_participants: true,
          include_analytics: true,
        }),
      );
    } catch (e) {
      if (e instanceof HTTPError && e.response.status == 404) throw notFound();
      else throw e;
    }
  },
  errorComponent({ error }) {
    if (error instanceof HTTPError && error.response.status == 403) {
      return (
        <Empty className="min-h-screen gap-6 border-0 bg-background p-6 md:p-12">
          <EmptyHeader className="max-w-[500px] gap-5">
            <EmptyMedia className="mb-0 size-auto">
              <img
                src={RestrictedImage}
                alt="Access restricted"
                className="h-auto w-[260px] max-w-full object-contain dark:hidden"
              />
              <img
                src={RestrictedDarkImage}
                alt="Access restricted"
                className="hidden h-auto w-[260px] max-w-full object-contain dark:block"
              />
            </EmptyMedia>
            <EmptyTitle className="text-lg leading-7 font-semibold text-[#181D27] dark:text-foreground">
              Access Restricted
            </EmptyTitle>
            <EmptyDescription className="max-w-[420px] text-sm leading-5 text-[#535862] dark:text-muted-foreground">
              You don't have permission to view this meeting's analysis. This
              resource is private to authorized organization members or invited
              participants.
            </EmptyDescription>
          </EmptyHeader>
        </Empty>
      );
    } else throw error;
  },
  notFoundComponent() {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="text-lg text-muted-foreground">Meeting not found</div>
      </div>
    );
  },
});

function ParticipantAnalysisTimeline({
  onSeekToVideo,
}: {
  onSeekToVideo?: () => void;
}) {
  const meetingCtx = useMeeting();
  const stateGroup = meetingCtx.details.state_group;

  const copyTranscript = useMutation({
    async mutationFn() {
      const allUtterances = await fetchAllMeetingUtterances(
        meetingCtx.details.id,
      );
      const transcript = allUtterances
        .map((utterance) => {
          const start = new Date(utterance.start_time);
          const end = new Date(start.getTime() + utterance.duration_ms);
          const relativeStart = normalizedDifferenceDuration(
            meetingCtx.reactiveState.startTime,
            start,
          );
          const relativeEnd = normalizedDifferenceDuration(
            meetingCtx.reactiveState.startTime,
            end,
          );

          return `${utterance.participant.full_name} (${formatMeetingTimestamp(relativeStart)}-${formatMeetingTimestamp(relativeEnd)})\n${utterance.transcription_text}\n`;
        })
        .join("\n");

      await navigator.clipboard.writeText(transcript);
    },
    onSettled() {
      setTimeout(copyTranscript.reset, 1000);
    },
  });

  return (
    <div className="flex flex-col gap-6">
      {stateGroup === "ENDED" && (
        <div className="flex justify-end">
          <Button
            className="h-10 w-40 px-3.5"
            aria-disabled={!copyTranscript.isIdle}
            onClick={() => {
              if (copyTranscript.isIdle) copyTranscript.mutate();
            }}
          >
            {copyTranscript.isIdle ? (
              <>
                <CopyIcon />
                Copy Transcript
              </>
            ) : copyTranscript.isPending ? (
              differenceInSeconds(copyTranscript.submittedAt, new Date()) >
              1 ? (
                <>
                  <LoadingSpinner />
                  Formatting Transcript
                </>
              ) : (
                <>
                  <CopyIcon />
                  Copy Transcript
                </>
              )
            ) : copyTranscript.isSuccess ? (
              <>
                <CheckIcon />
                Transcript Copied!
              </>
            ) : (
              <>
                <CrossIcon />
                Copy Error
              </>
            )}
          </Button>
        </div>
      )}
      <TranscriptionTimeline onSeek={onSeekToVideo} />
    </div>
  );
}

function ParticipantAnalysisSection({
  onSeekToVideo,
}: {
  onSeekToVideo?: () => void;
}) {
  const { error } = useMeeting();

  if (error) {
    return (
      <div>
        <span className="text-[#535862] dark:text-muted-foreground">
          Error during processing utterances. This meeting is likely outdated
          and has been recorded during early development
        </span>
        <pre>{error}</pre>
      </div>
    );
  }

  return <ParticipantAnalysisTimeline onSeekToVideo={onSeekToVideo} />;
}

// TODO: new design
function ReturnToBottom() {
  const { isAtBottom, scrollToBottom } = useStickToBottomContext();

  return (
    !isAtBottom && (
      <TooltipProvider delayDuration={100}>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              size="icon"
              className="absolute right-3 bottom-64 z-10 h-10 w-10 rounded-full bg-primary shadow-lg hover:shadow-xl"
              onClick={() => scrollToBottom()}
            >
              <ChevronDownIcon className="h-5 w-5" />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="top">Scroll to Bottom</TooltipContent>
        </Tooltip>
      </TooltipProvider>
    )
  );
}

function MeetingDetailStatusBadge({
  stateGroup,
}: {
  stateGroup: MeetingBot["state_group"];
}) {
  const label = formatStateGroupLabel(stateGroup);

  if (stateGroup === "ACTIVE") {
    return (
      <span className="inline-flex shrink-0 items-center rounded-full border border-[#FECDCA] bg-[#FEF3F2] px-2.5 py-0.5 text-xs font-medium text-[#D92D20] dark:border-red-400/30 dark:bg-red-500/15 dark:text-red-300">
        <span className="mr-1.5 inline-flex h-1.5 w-1.5 rounded-full bg-[#D92D20] dark:bg-red-400" />
        {label}
      </span>
    );
  }

  const styles: Record<string, string> = {
    ENDED:
      "border-[#ABEFC6] bg-[#ECFDF3] text-[#067647] dark:border-emerald-400/30 dark:bg-emerald-500/15 dark:text-emerald-300",
    POST_PROCESSING:
      "border-[#FEDF89] bg-[#FFFAEB] text-[#F79009] dark:border-amber-400/30 dark:bg-amber-500/15 dark:text-amber-300",
    SCHEDULED:
      "border-[#B9E6FE] bg-[#F0F9FF] text-[#026AA2] dark:border-sky-400/30 dark:bg-sky-500/15 dark:text-sky-300",
    PREPARATION:
      "border-[#FEDF89] bg-[#FFFAEB] text-[#F79009] dark:border-amber-400/30 dark:bg-amber-500/15 dark:text-amber-300",
    ERROR:
      "border-[#FECDCA] bg-[#FEF3F2] text-[#D92D20] dark:border-red-400/30 dark:bg-red-500/15 dark:text-red-300",
  };

  return (
    <span
      className={cn(
        "inline-flex shrink-0 items-center rounded-full border px-2.5 py-0.5 text-xs font-medium",
        styles[stateGroup] ??
          "border-[#E9EAEB] bg-[#FAFAFA] text-[#414651] dark:border-border dark:bg-muted/40 dark:text-foreground",
      )}
    >
      {label}
    </span>
  );
}

function VideoSection({
  videoUrl,
  onPlayed,
}: {
  videoUrl: string;
  onPlayed?: () => void;
}) {
  const videoRef = use(VideoContext);
  const [memoizedVideoUrl] = useState(videoUrl);

  const [hasStarted, setHasStarted] = useState(false);

  const handlePlay = useCallback(() => {
    setHasStarted(true);
    onPlayed?.();
  }, [onPlayed]);

  const handlePlayClick = useCallback(() => {
    if (videoRef?.current) {
      setHasStarted(true);
      onPlayed?.();
      videoRef.current.play().catch(() => {});
    }
  }, [onPlayed, videoRef]);

  return (
    <div className="relative w-full max-lg:h-60">
      <video
        width={1920}
        height={1080}
        ref={videoRef}
        src={memoizedVideoUrl}
        className="h-full w-full rounded-lg bg-gray-800 object-contain max-lg:h-60"
        controls={hasStarted}
        onPlay={handlePlay}
      />
      {!hasStarted && (
        <button
          onClick={handlePlayClick}
          className="checked: absolute inset-0 flex cursor-pointer items-center justify-center rounded-lg bg-black/30 transition-opacity hover:bg-black/40"
          aria-label="Play video"
        >
          <div className="flex h-20 w-20 items-center justify-center rounded-full bg-primary shadow-lg transition-transform hover:scale-110 dark:bg-[#DD2590]">
            <PlayIcon className="h-10 w-10 translate-x-0.5 text-white" />
          </div>
        </button>
      )}
    </div>
  );
}

function MeetingSummarySkeleton() {
  return (
    <div className="flex flex-col gap-6" aria-label="Loading meeting summary">
      <section className="flex flex-col gap-4">
        <Skeleton className="h-7 w-28" />
        <div className="space-y-3">
          <Skeleton className="h-5 w-full" />
          <Skeleton className="h-5 w-11/12" />
          <Skeleton className="h-5 w-4/5" />
        </div>
      </section>

      <section className="flex flex-col gap-4">
        <Skeleton className="h-7 w-24" />
        <Skeleton className="h-6 w-44" />
        <div className="space-y-3 rounded-md border border-[#D1E9FF] bg-[#F8F9FC] p-3 dark:border-border dark:bg-muted/40">
          <Skeleton className="h-5 w-full" />
          <Skeleton className="h-5 w-10/12" />
          <Skeleton className="h-5 w-3/4" />
        </div>
      </section>

      <section className="flex flex-col gap-4">
        <Skeleton className="h-7 w-32" />
        <div className="space-y-3 rounded-md border border-[#EBE9FE] bg-[#F8F9FC] p-3 dark:border-border dark:bg-muted/40">
          <Skeleton className="h-6 w-36" />
          <Skeleton className="h-5 w-full" />
          <Skeleton className="h-5 w-4/5" />
        </div>
      </section>
    </div>
  );
}

function SummarySection({ onCitationSeek }: { onCitationSeek?: () => void }) {
  const { meetingId } = Route.useParams();
  const meeting = useMeeting();
  const summary = useQuery({
    ...meetingSummaryQueryOptions(meetingId),
    refetchInterval: (query) => {
      if (
        query.state.error instanceof HTTPError &&
        query.state.error.response.status === 404
      ) {
        return false;
      }
      return meeting.details.state_group === "ENDED" ? false : 10000;
    },
    retry: (failureCount, error) => {
      if (error instanceof HTTPError && error.response.status === 404) {
        return false;
      }
      return failureCount < 3;
    },
  });

  if (summary.isLoading) return <MeetingSummarySkeleton />;
  else if (meeting.details.state_group !== "ENDED")
    return "You shouldn't be able to get here while meeting is not in Ended state";
  else if (meeting.details.meeting_start_time === null)
    return "Server Error: ended meeting has no meeting start time";
  else if (summary.isError) {
    if (
      summary.error instanceof HTTPError &&
      summary.error.response.status === 404
    )
      return (
        <div className="flex h-[300px] w-full items-center justify-center px-4 sm:h-[400px] md:h-[500px]">
          <div className="text-center text-sm text-muted-foreground sm:text-base md:text-lg">
            Meeting summary is being created...
          </div>
        </div>
      );
    else
      return (
        <div className="flex h-[300px] w-full items-center justify-center px-4 sm:h-[400px] md:h-[500px]">
          <div className="text-center text-sm text-muted-foreground sm:text-base md:text-lg">
            Error loading summary
          </div>
        </div>
      );
  } else if (summary.isSuccess) {
    if (summary.data.version === 1)
      return (
        <Empty>
          <EmptyHeader>
            <EmptyMedia variant="icon">
              <SaveOffIcon />
            </EmptyMedia>
          </EmptyHeader>
          <EmptyDescription>
            No summary is available for this meeting due to the meeting having
            happened during early development. If you're seeing this message
            outside of the development environment, please contact Polygraf.
          </EmptyDescription>
        </Empty>
      );
    return (
      <MeetingSummaryPanel
        summary={summary.data}
        meetingStartTime={meeting.details.meeting_start_time}
        onCitationSeek={onCitationSeek}
      />
    );
  }
}

const platformNameMap = {
  google: "Google Meet",
  zoom: "Zoom",
  teams: "Microsoft Teams",
} as const;

function ScheduledMeetingInfo() {
  const { meetingId } = Route.useParams();
  const theme = useTheme();
  const scheduledImage = theme === "dark" ? ScheduledDarkImage : ScheduledImage;

  const qOptions = useMemo(
    () =>
      meetingBotQueryOptions(meetingId, {
        include_participants: true,
      }),
    [meetingId],
  );

  const meeting = useSuspenseQuery(qOptions);
  return (
    <div className="flex min-h-screen w-full flex-col bg-white dark:bg-background">
      <div className="shrink-0 border-b border-[#E9EAEB] px-4 py-4 md:px-6 lg:px-8 dark:border-border">
        <Link
          to="/meetings/scheduled"
          className="flex min-w-0 items-center gap-4 text-sm font-semibold text-[#181D27] lg:hidden dark:text-foreground"
        >
          <ChevronLeftIcon className="h-4 w-4 shrink-0" />
          <span className="truncate">{meeting.data.meeting_name}</span>
        </Link>

        <nav className="hidden min-w-0 items-center gap-2 text-sm lg:flex">
          <Link
            to="/meetings/scheduled"
            className="shrink-0 font-semibold text-[#DD2590] hover:text-[#DD2590]/80"
          >
            Scheduled Meetings
          </Link>
          <ChevronRightIcon className="h-4 w-4 shrink-0 text-[#717680] dark:text-muted-foreground" />
          <span className="truncate font-semibold text-[#414651] dark:text-foreground">
            {meeting.data.meeting_name}
          </span>
        </nav>
      </div>

      <Empty className="min-h-0 flex-1 gap-5 border-0 bg-white p-6 md:p-12 dark:bg-background">
        <EmptyHeader className="max-w-[500px] gap-5">
          <EmptyMedia className="mb-0 size-auto">
            <img
              src={scheduledImage}
              alt=""
              width={414}
              height={316}
              className="h-[316px] w-[414px] max-w-full object-contain"
            />
          </EmptyMedia>
          <EmptyTitle className="text-lg leading-7 font-semibold text-[#181D27] dark:text-foreground">
            Awaiting Meeting Start
          </EmptyTitle>
          <EmptyDescription className="max-w-[490px] text-sm leading-5 text-[#535862] dark:text-muted-foreground">
            This meeting will take place{" "}
            {meeting.data.meeting_start_time !== null
              ? "on " +
                new Date(meeting.data.meeting_start_time).toLocaleString()
              : "in the future"}{" "}
            {meeting.data.platform in platformNameMap
              ? "via " + platformNameMap[meeting.data.platform]
              : null}
            . Your security analysis, PII alerts, and session summary will be
            generated here once the meeting begins.
          </EmptyDescription>
        </EmptyHeader>
      </Empty>
    </div>
  );
}

function MeetingPageRight({ onSeekToVideo }: { onSeekToVideo?: () => void }) {
  const [activeTab, setActiveTab] = useState<MeetingDetailTab>("transcription");

  const meetingCtx = useMeeting();
  const summaryDisabled =
    meetingCtx.details.state_group !== "ENDED" ||
    !meetingCtx.details.permissions.can_view_summary;

  const shouldStickToBottom = meetingCtx.history.direction === "up";

  const ScrollParent = shouldStickToBottom ? StickToBottom : "div";
  const ScrollChild = shouldStickToBottom ? StickToBottom.Content : "div";

  return (
    <>
      {/* TODO: use <Tabs> here */}
      <ScrollParent
        initial="instant"
        className="hide-scrollbar min-w-0 md:min-h-0 md:overflow-y-auto md:overscroll-contain md:pl-1"
      >
        <ScrollChild scrollClassName="hide-scrollbar">
          <div className="flex flex-col gap-6 rounded-[10px] border border-[#D5D7DA] bg-white p-5 dark:border-border dark:bg-card">
            {/* TODO: move the tab selector out of the scroll content */}
            <MeetingDetailTabs
              activeTab={activeTab}
              onChange={setActiveTab}
              summaryDisabled={summaryDisabled}
            />

            {activeTab === "transcription" ? (
              <ParticipantAnalysisSection onSeekToVideo={onSeekToVideo} />
            ) : (
              <SummarySection onCitationSeek={onSeekToVideo} />
            )}
          </div>
        </ScrollChild>
        {shouldStickToBottom && <ReturnToBottom />}
      </ScrollParent>
    </>
  );
}

function MeetingPageInternal() {
  const navigate = Route.useNavigate();
  const { from, scope } = Route.useSearch();
  const userInfo = useSuspenseQuery(userInfoQueryOptions);

  const meetingCtx = useMeeting();
  const theme = useTheme();
  const isDark = theme === "dark";
  const preparationImage = isDark ? PreparationDarkImage : PreparationImage;
  const processImage = isDark ? PreparationDarkImage : ProcessImage;
  const errorImage = isDark ? ErrorDarkImage : ErrorImage;
  const isSharedMeetingView = from === "shared" || isGuest(userInfo.data);
  const canManageMeeting = !isSharedMeetingView;
  const meetingsListPath = isSharedMeetingView
    ? "/meetings/shared"
    : "/meetings";
  const meetingsListLabel = isSharedMeetingView ? "Shared with Me" : "Meetings";
  invariant(
    meetingCtx.details.participants !== null,
    "Participants list should have been received from back",
  );

  const [isRejoinWarningOpen, setIsRejoinWarningOpen] = useState(false);
  const [isShareOpen, setIsShareOpen] = useState(false);
  const [videoHasPlayed, setVideoHasPlayed] = useState(false);
  const videoContainerRef = useRef<HTMLDivElement>(null);

  const scrollToVideo = useCallback(() => {
    videoContainerRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  }, []);

  const rejoinMeeting = useRejoinMeetingMutation({
    onSuccess: (newMeeting) => {
      navigate({
        to: "/meetings/$meetingId",
        params: { meetingId: newMeeting.id },
        reloadDocument: true,
      });
    },
  });

  // const { utterances, isLoading, isError } = useUtterances({
  //   meeting: meetingCtx.details,
  // });

  const handleVideoPlayed = useCallback(() => {
    setVideoHasPlayed(true);
  }, []);

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(meetingCtx.details.meeting_url);
      toast.success("Meeting link copied");
    } catch {
      toast.error("Failed to copy link");
    }
  };

  const handleRejoin = useCallback(() => {
    if (rejoinMeeting.isPending) return;

    startTransition(() => {
      const newDate = new Date();
      const baseDate =
        meetingCtx.details.join_at ?? meetingCtx.details.created_at;
      const normalizedDuration = normalizedDifferenceDuration(
        baseDate,
        newDate,
      );
      const differenceInHours = duration.toHours(normalizedDuration);

      if (differenceInHours > 6) {
        setIsRejoinWarningOpen(true);
        return;
      } else rejoinMeeting.mutate(meetingCtx.details.id);
    });
  }, [
    meetingCtx.details.created_at,
    meetingCtx.details.id,
    meetingCtx.details.join_at,
    rejoinMeeting,
  ]);

  const header = (
    <div className="shrink-0 border-b border-[#E9EAEB] bg-white px-4 py-4 md:px-6 lg:px-8 dark:border-border dark:bg-card">
      <div className="flex flex-col gap-3">
        <Link
          to={meetingsListPath}
          search={isSharedMeetingView ? {} : { scope }}
          className="flex min-w-0 items-center gap-4 text-sm font-semibold text-[#181D27] lg:hidden dark:text-foreground"
        >
          <ChevronLeftIcon className="h-4 w-4 shrink-0" />
          <span className="truncate">{meetingCtx.details.meeting_name}</span>
        </Link>

        <nav className="hidden min-w-0 items-center gap-2 text-sm lg:flex">
          <Link
            to={meetingsListPath}
            search={isSharedMeetingView ? {} : { scope }}
            className="shrink-0 font-medium text-[#DD2590] hover:text-[#DD2590]/80"
          >
            {meetingsListLabel}
          </Link>
          <ChevronRightIcon className="h-4 w-4 shrink-0 text-[#717680] dark:text-muted-foreground" />
          <span className="truncate font-semibold text-[#414651] dark:text-foreground">
            {meetingCtx.details.meeting_name}
          </span>
        </nav>

        <div className="flex items-center gap-3 lg:hidden">
          {canManageMeeting && (
            <>
              <Button
                variant="outline"
                size="icon"
                className="h-10 w-10 shrink-0 rounded-lg border-[#D5D7DA] dark:border-border"
                onClick={() => void handleCopyLink()}
                aria-label="Copy meeting link"
              >
                <Link2 className="h-5 w-5" />
              </Button>
              <Button
                variant="outline"
                size="icon"
                className="h-10 w-10 shrink-0 rounded-lg border-[#D5D7DA] dark:border-border"
                onClick={() => setIsShareOpen(true)}
                aria-label="Share meeting"
              >
                <Share2 className="h-5 w-5" />
              </Button>
              {meetingCtx.details.state_group === "ENDED" && (
                <Button
                  size="sm"
                  className="h-10 flex-1 rounded-lg bg-[#DD2590] text-sm font-medium text-white hover:bg-[#DD2590]/90"
                  aria-disabled={rejoinMeeting.isPending}
                  onClick={handleRejoin}
                >
                  Resend Agent
                </Button>
              )}
            </>
          )}
        </div>

        <div className="flex flex-col gap-3 lg:hidden">
          <div className="flex items-center justify-between gap-3">
            <span className="inline-flex min-w-0 items-center gap-3 text-sm font-medium text-[#535862] dark:text-muted-foreground">
              <ClockIcon className="h-5 w-5 shrink-0" />
              {meetingCtx.details.meeting_start_time === null
                ? "--"
                : new Date(
                    meetingCtx.details.meeting_start_time,
                  ).toLocaleString("en-US", {
                    month: "short",
                    day: "numeric",
                    year: "numeric",
                    hour: "numeric",
                    minute: "2-digit",
                  })}
            </span>
            <MeetingDetailStatusBadge
              stateGroup={meetingCtx.details.state_group}
            />
          </div>

          {meetingCtx.details.participants.length > 0 && (
            <span className="inline-flex items-center gap-3 text-sm font-medium text-[#535862] dark:text-muted-foreground">
              <Users className="h-5 w-5 shrink-0" />
              {meetingCtx.details.participants.length} participants
            </span>
          )}

          {meetingCtx.details.bot_name && (
            <p className="text-sm leading-5 wrap-break-word text-[#535862] dark:text-muted-foreground">
              <span className="text-[#717680] dark:text-muted-foreground">
                Agent:{" "}
              </span>
              <span className="font-medium">{meetingCtx.details.bot_name}</span>
            </p>
          )}
        </div>

        <div className="hidden flex-col gap-3 sm:flex-row sm:items-center sm:justify-between lg:flex">
          <div className="flex min-w-0 flex-wrap items-center gap-x-4 gap-y-2 text-sm text-[#717680] dark:text-muted-foreground">
            <span className="inline-flex items-center gap-1.5">
              <ClockIcon className="h-4 w-4 shrink-0" />
              {meetingCtx.details.meeting_start_time === null
                ? "--"
                : new Date(
                    meetingCtx.details.meeting_start_time,
                  ).toLocaleString("en-US", {
                    month: "short",
                    day: "numeric",
                    year: "numeric",
                    hour: "numeric",
                    minute: "2-digit",
                  })}
            </span>

            {meetingCtx.details.participants.length > 0 && (
              <span className="inline-flex items-center gap-1.5">
                <Users className="h-4 w-4 shrink-0" />
                {meetingCtx.details.participants.length} participants
              </span>
            )}

            {meetingCtx.details.bot_name && (
              <span className="min-w-0">
                <span className="text-[#717680] dark:text-muted-foreground">
                  Agent:{" "}
                </span>
                <span className="text-[#414651] dark:text-foreground">
                  {meetingCtx.details.bot_name}
                </span>
              </span>
            )}

            <MeetingDetailStatusBadge
              stateGroup={meetingCtx.details.state_group}
            />
          </div>

          {canManageMeeting && (
            <div className="flex shrink-0 flex-wrap items-center gap-2">
              <Button
                variant="outline"
                size="icon"
                className="h-10 w-10 shrink-0 rounded-lg border-[#D5D7DA] text-[#414651] dark:border-border dark:text-foreground"
                onClick={() => void handleCopyLink()}
                aria-label="Copy meeting link"
              >
                <Link2 className="h-5 w-5" />
              </Button>
              <Button
                variant="outline"
                size="icon"
                className="h-10 w-10 shrink-0 rounded-lg border-[#D5D7DA] text-[#414651] dark:border-border dark:text-foreground"
                onClick={() => setIsShareOpen(true)}
                aria-label="Share meeting"
              >
                <Share2 className="h-5 w-5" />
              </Button>
              {meetingCtx.details.state_group === "ENDED" && (
                <Button
                  size="lg"
                  className="h-10 gap-2 rounded-lg bg-[#DD2590] text-sm font-medium text-white hover:bg-[#DD2590]/90"
                  aria-disabled={rejoinMeeting.isPending}
                  onClick={handleRejoin}
                >
                  Resend Agent
                </Button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const dialogs = canManageMeeting && (
    <>
      <ShareMeetingDialog
        meeting={meetingCtx.details}
        open={isShareOpen}
        onOpenChange={setIsShareOpen}
      />

      <AlertDialog
        open={isRejoinWarningOpen}
        onOpenChange={setIsRejoinWarningOpen}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Rejoin Meeting</AlertDialogTitle>
            <AlertDialogDescription>
              More than 6 hours have passed since this meeting. Do you want to
              Resend Agent anyway?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction
              onClick={() => rejoinMeeting.mutate(meetingCtx.details.id)}
              disabled={rejoinMeeting.isPending}
            >
              Resend Agent
            </AlertDialogAction>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );

  const renderStateEmpty = ({
    image,
    imageAlt,
    title,
    description,
    showLoading = false,
    action,
  }: {
    image: string;
    imageAlt: string;
    title: string;
    description: string;
    showLoading?: boolean;
    action?: React.ReactNode;
  }) => (
    <>
      <div className="flex w-full flex-col bg-background md:h-full md:min-h-0">
        {header}

        <Empty className="min-h-0 flex-1 gap-6 border-0 bg-background p-6 md:p-12">
          <EmptyHeader className="max-w-[500px] gap-5">
            <EmptyMedia className="mb-0 size-auto">
              <img
                src={image}
                alt={imageAlt}
                className="h-[172px] w-[260px] max-w-full object-contain"
              />
            </EmptyMedia>
            <EmptyTitle className="text-lg leading-7 font-semibold text-[#181D27] dark:text-foreground">
              {title}
            </EmptyTitle>
            <EmptyDescription className="max-w-[420px] text-sm leading-5 text-[#535862] dark:text-muted-foreground">
              {description}
            </EmptyDescription>
          </EmptyHeader>
          {showLoading && (
            <div className="flex items-center gap-2 text-sm font-medium text-[#DD2590]">
              <LoadingSpinner className="text-[#DD2590]" size={20} />
              <span>Loading...</span>
            </div>
          )}
          {action}
        </Empty>
      </div>

      {dialogs}
    </>
  );

  if (meetingCtx.details.state_group === "PREPARATION") {
    return renderStateEmpty({
      image: preparationImage,
      imageAlt: "Agent is joining the call...",
      title: "Agent is joining the call...",
      description:
        "Once the meeting starts and the bot connects, the complete analysis and overview of your meeting will be displayed here.",
      showLoading: true,
    });
  }

  if (meetingCtx.details.state_group === "POST_PROCESSING") {
    return renderStateEmpty({
      image: processImage,
      imageAlt: "Processing meeting",
      title: "Processing Your Meeting",
      description:
        "We're analyzing the session and generating your security report. The full overview will appear here shortly.",
      showLoading: true,
    });
  }

  if (meetingCtx.details.state_group === "ERROR") {
    return renderStateEmpty({
      image: errorImage,
      imageAlt: "Agent couldn't join",
      title: "Agent couldn't join",
      description: "Meeting link invalid or permissions required",
      action: canManageMeeting ? (
        <Button
          size="lg"
          className="h-10 gap-2 rounded-lg bg-[#DD2590] text-sm font-medium text-white hover:bg-[#DD2590]/90"
          aria-disabled={rejoinMeeting.isPending}
          onClick={handleRejoin}
        >
          {rejoinMeeting.isPending ? (
            <>
              <LoadingSpinner className="text-white" size={20} />
              Resending Agent...
            </>
          ) : (
            "Resend Agent"
          )}
        </Button>
      ) : undefined,
    });
  }

  return (
    <>
      <div className="flex w-full flex-col bg-background md:h-full md:min-h-0">
        {header}

        <div className="grid min-h-0 flex-1 grid-cols-1 gap-6 md:grid-cols-[minmax(0,42%)_minmax(0,1fr)] md:overflow-hidden md:p-6 xl:grid-cols-[minmax(0,40%)_minmax(0,1fr)]">
          <div className="hide-scrollbar contents md:flex md:min-h-0 md:flex-col md:gap-6 md:overflow-y-auto md:overscroll-contain md:pr-1">
            {meetingCtx.details.state_group === "ACTIVE" ? (
              <div className="flex aspect-video w-full flex-col items-center justify-center gap-4 rounded-[10px] border border-[#E9EAEB] bg-background p-6 text-center dark:border-border">
                <img
                  src={preparationImage}
                  alt="Recording in progress"
                  className="h-[132px] w-[200px] max-w-full object-contain"
                />
                <div className="flex flex-col gap-1">
                  <p className="text-lg leading-7 font-semibold text-[#181D27] dark:text-foreground">
                    Recording in Progress
                  </p>
                  <p className="text-sm leading-5 text-[#535862] dark:text-muted-foreground">
                    Meeting Guard is actively recording this live session
                  </p>
                </div>
              </div>
            ) : meetingCtx.details.recording &&
              meetingCtx.details.permissions.can_view_recording ? (
              <div
                ref={videoContainerRef}
                className={cn(
                  "w-full scroll-mt-4 self-start",
                  videoHasPlayed &&
                    "max-md:sticky max-md:top-0 max-md:z-30 max-md:shadow-md",
                )}
              >
                <div className="overflow-hidden rounded-[10px] border border-[#E9EAEB] bg-[#181D27] dark:border-border">
                  <VideoSection
                    videoUrl={meetingCtx.details.recording.url}
                    onPlayed={handleVideoPlayed}
                  />
                </div>
              </div>
            ) : (
              <div className="flex aspect-video w-full items-center justify-center rounded-[10px] border border-[#E9EAEB] bg-[#D5D7DA] dark:border-border dark:bg-muted">
                <div className="relative flex h-20 w-20 items-center justify-center rounded-full bg-[#F5F5F5] shadow-[0_0_40px_10px_rgba(236,72,153,0.35)] dark:bg-card">
                  <LockIcon
                    className="h-8 w-8 text-[#414651] dark:text-foreground"
                    strokeWidth={2}
                  />
                </div>
              </div>
            )}

            {/* <MeetingOverallCard /> */}

            <MeetingParticipantsCard />
          </div>

          <MeetingPageRight onSeekToVideo={scrollToVideo} />
        </div>
      </div>

      {dialogs}
    </>
  );
}

function RouteComponent() {
  const { meetingId } = Route.useParams();

  const qOptions = useMemo(
    () =>
      meetingBotQueryOptions(meetingId, {
        include_participants: true,
      }),
    [meetingId],
  );

  const meeting = useSuspenseQuery(qOptions);

  invariant(
    meeting.data.participants !== null,
    "Participants list should have been received from back",
  );

  const [waiting, setWaiting] = useState(
    meeting.data.state_group === "SCHEDULED",
  );
  const qClient = useQueryClient();

  useEffect(() => {
    const ac = new AbortController();
    if (!waiting) return;

    async function effect() {
      while (!ac.signal.aborted) {
        await new Promise((r) => setTimeout(r, 5000));

        const now = new Date();
        const meetingStart = new Date(
          meeting.data.meeting_start_time ?? Date.now(),
        );
        const timeDiff = differenceInSeconds(now, meetingStart);

        if (isPast(meetingStart) || timeDiff < 60) {
          const meeting = await qClient.fetchQuery(qOptions);
          if (!["SCHEDULED", "PREPARATION"].includes(meeting.state_group)) {
            if (document.startViewTransition as unknown)
              document.startViewTransition(() =>
                flushSync(() => setWaiting(false)),
              );
            // backwards compat with older browsers
            else setWaiting(false);
            return;
          }
        }
      }
    }
    void effect();

    return () => ac.abort();
  }, [waiting, meeting.data.state_group]);

  const videoRef = useRef<ComponentRef<"video">>(null);

  if (meeting.isError) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="text-lg text-muted-foreground">
          Error loading meeting
        </div>
      </div>
    );
  }

  if (waiting) return <ScheduledMeetingInfo />;

  return (
    <VideoContext value={videoRef}>
      <MeetingProvider meetingId={meetingId}>
        <MeetingPageInternal />
      </MeetingProvider>
    </VideoContext>
  );
}
